/* eslint-disable no-console */
export interface PermissionType {
    name: string;
    featureEnabled: boolean;
    permissions?: Array<{
      access: string;
      male: boolean;
      female: boolean;
    }>;
    male?: boolean;
    female?: boolean;
  }
export interface Submodule {
    name: string;
    featureEnabled: boolean;
    type?: PermissionType[];
  }
export interface Module {
    name: string;
    featureEnabled: boolean;
    submodules: Submodule[];
  }
export interface Permissions {
    modules: Module[];
  }

// Redirection priority order with paths

const redirectionPriority: { name: string; path: string }[] = [
  // Admin
  { name: 'Calendar', path: 'admin/appointments' },
  { name: 'Registration', path: 'admin/registration/patient-list' },
  { name: 'Billing', path: 'admin/billing/package' },
  { name: 'Pharmacy Dispense', path: 'admin/pharmacy-dispense' },
  { name: 'Inventory', path: 'admin/inventory/item-management/stock' },
  // Clinical
  { name: 'Dashboard', path: 'clinical/dashboard' },
  { name: 'Patient', path: 'clinical/patients' },
  // Lab
  { name: 'Dashboard', path: 'lab/dashboard' },
  { name: 'Embryology', path: 'lab/embryology' },
  { name: 'Patient', path: 'lab/embryology' },
  { name: 'Andrology', path: 'lab/andrology' },
];

const clinicalSummaryPriority: { name: string; path: (patientId: number) => string }[] = [
  { name: 'Summary', path: (id) => `/clinical/patients/summary/${id}` },
  { name: 'Medical History', path: (id) => `/clinical/patients/medical-history/${id}` },
  { name: 'HPI', path: (id) => `/clinical/patients/hpi/${id}` },
  { name: 'Examination', path: (id) => `/clinical/patients/examination/${id}` },
  { name: 'Investigations', path: (id) => `/clinical/patients/investigation/${id}` },
  { name: 'Treatment Plan', path: (id) => `/clinical/patients/treatment-plan/${id}` },
  { name: 'Cycle Overview', path: (id) => `/clinical/patients/cycle-overview/${id}` },
  { name: 'Stimulation', path: (id) => `/clinical/patients/stimulation/${id}` },
  { name: 'Surgery', path: (id) => `/clinical/patients/surgery/${id}` },
  { name: 'Outcome', path: (id) => `/clinical/patients/outcome/${id}` },
  { name: 'Document', path: (id) => `/clinical/patients/document/${id}` },
];
/**
 * Redirects to the first enabled clinical summary section for a patient
 * @param modules Permission modules array
 * @param patientId Patient ID
 * @param navigateTo Navigation function
 */
export function redirectToFirstClinicalSummary(modules: Module[], patientId: number, navigateTo: (path: string) => void) {
  if (patientId > 0) {
    // Find Clinical module
    const clinicalModule = modules.find(m => m.name === 'Clinical' && m.featureEnabled);
    if (!clinicalModule) return;
    // Find first enabled submodule in priority order
    for (const item of clinicalSummaryPriority) {
      const submodule = clinicalModule.submodules.find(s => s.name === item.name && s.featureEnabled);
      if (submodule) {
        navigateTo(item.path(patientId));
        return;
      }
    }
  }
}

// Lab summary redirection priority order with paths
const embryologyPriority: { name: string; path: (patientId: number) => string }[] = [
  { name: 'Oocyte Grading', path: (id) => `/lab/embryology/oocyte-grading/${id}` },
  { name: 'Embryo Culture', path: (id) => `/lab/embryology/embryo-culture/${id}` },
  { name: 'AI Embryo', path: (id) => `/lab/embryology/ai-embryo/${id}` },
  { name: 'PGT', path: (id) => `/lab/embryology/pgta/${id}` },
  { name: 'Cryo Thawing', path: (id) => `/lab/embryology/cryo-thawing/${id}` },
];

/**
 * Redirects to the first enabled lab  section for a patient
 * @param modules Permission modules array
 * @param patientId Patient ID
 * @param navigateTo Navigation function
 */
export function redirectToFirstEmbryology(modules: Module[], patientId: number, navigateTo: (path: string) => void) {
  if (patientId > 0) {
    // Find Lab module
    const labModule = modules.find(m => m.name === 'Lab' && m.featureEnabled);
    if (!labModule) return;
    // Find first enabled submodule in priority order
    for (const item of embryologyPriority) {
      const submodule = labModule.submodules.find(s => s.name === item.name && s.featureEnabled);
      if (submodule) {
        navigateTo(item.path(patientId));
        return;
      }
    }
  }
}

// Andrology summary redirection priority order with paths
const andrologySummaryPriority: { name: string; path: (patientId: number) => string }[] = [
  { name: 'Semen Analysis', path: (id) => `/lab/andrology/semen-analysis/${id}` },
  { name: 'Semen Processing', path: (id) => `/lab/andrology/semen-processing/${id}` },
  { name: 'Semen Freezing', path: (id) => `/lab/andrology/semen-freezing/${id}` },
  { name: 'Semen Thawing', path: (id) => `/lab/andrology/semen-thawing/${id}` },
  { name: 'IUI', path: (id) => `/lab/andrology/IUI/${id}` },
];

/**
 * Redirects to the first enabled andrology summary section for a patient
 * @param modules Permission modules array
 * @param patientId Patient ID
 * @param navigateTo Navigation function
 */
export function redirectToFirstAndrology(modules: Module[], patientId: number, navigateTo: (path: string) => void) {
  if (patientId > 0) {
    // Find Lab module
    const labModule = modules.find(m => m.name === 'Lab' && m.featureEnabled);
    if (!labModule) return;
    // Find first enabled submodule in priority order
    for (const item of andrologySummaryPriority) {
      const submodule = labModule.submodules.find(s => s.name === item.name && s.featureEnabled);
      if (submodule) {
        navigateTo(item.path(patientId));
        return;
      }
    }
  }
}

// Get permissions from localStorage
export function getPermissionsFromLocalStorage(key: string = 'permissions'): Permissions | null {
  const data = localStorage.getItem(key);
  if (!data) return null;
  try {
    return JSON.parse(data) as Permissions;
  } catch {
    return null;
  }
}

// Check if user has access to a module
export function hasModuleAccess(modules: Module[], moduleName: string): boolean {
  const module = modules.find(m => m.name === moduleName);
  return !!module && module.featureEnabled;
}

export function checkModuleOrSubmodulePermission(
  moduleName: string,
  submoduleName?: string,
  typeName?: string
): { AllowPermissions: boolean } | null {
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) return null;
  let allow = false;
  const module = permissions.modules.find(m => m.name === moduleName && m.featureEnabled);
  if (module) {
    if (!submoduleName) {
      allow = true;
    } else {
      const submodule = module.submodules.find(s => s.name === submoduleName && s.featureEnabled);
      if (submodule) {
        if (!typeName || typeName.trim() === '') {
          allow = true;
        } else {
          allow = !!submodule.type?.find(t => t.name === typeName && t.featureEnabled);
        }
      }
    }
  }
  return { AllowPermissions: allow };
}

// Get first accessible route for a module based on priority
export function getFirstAccessibleRoute(modules: Module[], moduleName: string): string | null {
  const module = modules.find(m => m.name === moduleName && m.featureEnabled);
  if (!module) return null;
  for (const route of redirectionPriority) {
    if (module.submodules.some(sub => sub.name === route.name && sub.featureEnabled)) {
      return route.path;
    }
  }
  return null;
}

// Updated utility function to check if a department is featureEnabled under the Admin module
export function isDepartmentFeatureEnabled(departmentName: string): boolean {
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) {
    console.log('No permissions found in localStorage.'); // Log missing permissions
    return false;
  }

  const adminModule = permissions.modules.find(module => module.name === 'Admin');
  if (!adminModule) {
    console.log('Admin module not found in permissions.'); // Log missing Admin module
    return false;
  }

  // Check if the department exists under the "Calendar" submodule
  const calendarSubmodule = adminModule.submodules.find(submodule => submodule.name === 'Calendar');
  if (!calendarSubmodule) {
    console.log('Calendar submodule not found under Admin module.'); // Log missing Calendar submodule
    return false;
  }

  // Check if the department exists within the type array of the Calendar submodule
  if (calendarSubmodule.type) {
    const matchingType = calendarSubmodule.type.find(type => type.name === departmentName);
    if (matchingType) {
      // console.log(`Department '${departmentName}' featureEnabled status in Calendar submodule: ${matchingType.featureEnabled}`); // Log featureEnabled status
      return matchingType.featureEnabled;
    }
  }

  return false;
}

// Main login redirection logic
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getLoginRedirect(permissionsFromParam?: any): { module: string; route: string | null } {
  const permissions = permissionsFromParam || getPermissionsFromLocalStorage(); // Use passed permissions or fallback to localStorage
  console.log('Permissions used for redirection:', permissions); // Log permissions

  if (!permissions) {
    console.log('No permissions found, returning default redirection.'); // Log fallback case
    return { module: '', route: null };
  }

  const modules = permissions.modules;
  console.log('Modules available for redirection:', modules); // Log available modules

  if (hasModuleAccess(modules, 'Admin')) {
    const route = getFirstAccessibleRoute(modules, 'Admin');
    console.log('Redirecting to Admin module with route:', route); // Log redirection details
    return { module: 'Admin', route };
  }
  if (hasModuleAccess(modules, 'Clinical')) {
    const route = getFirstAccessibleRoute(modules, 'Clinical');
    console.log('Redirecting to Clinical module with route:', route); // Log redirection details
    return { module: 'Clinical', route };
  }
  if (hasModuleAccess(modules, 'Lab')) {
    const route = getFirstAccessibleRoute(modules, 'Lab');
    console.log('Redirecting to Lab module with route:', route); // Log redirection details
    return { module: 'Lab', route };
  }
  if (hasModuleAccess(modules, 'Billing')) {
    const route = getFirstAccessibleRoute(modules, 'Billing') || 'admin/billing/package';
    console.log('Redirecting to Billing module with route:', route); // Log redirection details
    return { module: 'Billing', route };
  }
  if (hasModuleAccess(modules, 'Inventory')) {
    const route = getFirstAccessibleRoute(modules, 'Inventory') || 'admin/inventory/item-management/stock';
    console.log('Redirecting to Inventory module with route:', route); // Log redirection details
    return { module: 'Inventory', route };
  }
  // Fallback to Settings or first available module
  if (hasModuleAccess(modules, 'Settings')) {
    const route = getFirstAccessibleRoute(modules, 'Settings');
    console.log('Redirecting to Settings module with route:', route); // Log redirection details
    return { module: 'Settings', route };
  }

  console.log('No accessible modules found, returning default redirection.'); // Log fallback case
  return { module: '', route: null };
}
// Usage example:
// import { getLoginRedirect } from 'src/utils/permissionService';
// const { module, route } = getLoginRedirect();
// Redirect user to the module and route


// Check if a user has permission for a specific action
export function hasActionPermission(
  permissions: Permissions,
  moduleName: string,
  submoduleName: string,
  typeName: string,
  action: string,
  gender?: 'male' | 'female'
): boolean {
  const module = permissions.modules.find(m => m.name === moduleName);
  if (!module || !module.featureEnabled) return false;

  const submodule = module.submodules.find(s => s.name === submoduleName);
  if (!submodule || !submodule.featureEnabled) return false;

  // Handle empty or missing typeName
  if (!typeName || typeName.trim() === '') {
    console.warn(`Type name is empty or not provided for module '${moduleName}', submodule '${submoduleName}'.`);
    return false;
  }

  const type = submodule.type?.find(t => t.name === typeName);
  if (!type || !type.featureEnabled) return false;

  // If gender is provided, check gender-specific permissions
  if (gender) {
    return type.permissions?.some(p => p.access === action && p[gender]) || false;
  }
  return type.permissions?.some(p => p.access === action) || false;
}

export interface ButtonState {
  canView: boolean;
  canEdit: boolean;
  canAdd: boolean;
  canPrint?: boolean;
  isMale?: boolean;
  isFemale?: boolean;
}

export function getButtonStates(
  moduleName: string,
  submoduleName: string,
  typeName: string,
  gender?: 'male' | 'female'
): ButtonState {
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) {
    console.warn('No permissions found in localStorage.');
    return { canView: false, canEdit: false, canAdd: false, isMale: false, isFemale: false };
  }

  const module = permissions.modules.find(m => m.name === moduleName);
  if (!module || !module.featureEnabled) {
    console.warn(`Module '${moduleName}' is not accessible or featureEnabled is false.`);
    return { canView: false, canEdit: false, canAdd: false, isMale: false, isFemale: false };
  }

  const submodule = module.submodules.find(s => s.name === submoduleName);
  if (!submodule || !submodule.featureEnabled) {
    console.warn(`Submodule '${submoduleName}' is not accessible or featureEnabled is false.`);
    return { canView: false, canEdit: false, canAdd: false, isMale: false, isFemale: false };
  }

  // Handle empty or missing typeName
  if (!typeName || typeName.trim() === '') {
    console.warn(`Type name is empty or not provided for module '${moduleName}', submodule '${submoduleName}'.`);
    return { canView: false, canEdit: false, canAdd: false, isMale: false, isFemale: false };
  }

  const type = submodule.type?.find(t => t.name === typeName);
  if (!type || !type.featureEnabled) {
    console.warn(`Type '${typeName}' is not accessible or featureEnabled is false.`);
    return { canView: false, canEdit: false, canAdd: false, isMale: false, isFemale: false };
  }

  const canView = hasActionPermission(permissions, moduleName, submoduleName, typeName, 'view', gender);
  const canEdit = hasActionPermission(permissions, moduleName, submoduleName, typeName, 'edit', gender);
  const canAdd = hasActionPermission(permissions, moduleName, submoduleName, typeName, 'add', gender);
  const canPrint = hasActionPermission(permissions, moduleName, submoduleName, typeName, 'print', gender);

  // isMale/isFemale: true if any permission for this type allows male/female
  const isMale = type.permissions?.some(p => p.male) || false;
  const isFemale = type.permissions?.some(p => p.female) || false;

  return { canView, canEdit, canAdd, canPrint, isMale, isFemale };
}

// Returns button states for all types in a submodule
export function getAllButtonStates(
  moduleName: string,
  submoduleName: string,
  gender?: 'male' | 'female'
): Record<string, ButtonState> {
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) {
    console.warn('No permissions found in localStorage.');
    return {};
  }

  const module = permissions.modules.find(m => m.name === moduleName);
  if (!module || !module.featureEnabled) {
    console.warn(`Module '${moduleName}' is not accessible or featureEnabled is false.`);
    return {};
  }

  const submodule = module.submodules.find(s => s.name === submoduleName);
  if (!submodule || !submodule.featureEnabled || !submodule.type) {
    console.warn(`Submodule '${submoduleName}' is not accessible or featureEnabled is false.`);
    return {};
  }

  const result: Record<string, ButtonState> = {};
  for (const type of submodule.type) {
    if (!type.featureEnabled) continue;
    const canView = hasActionPermission(permissions, moduleName, submoduleName, type.name, 'view', gender);
    const canEdit = hasActionPermission(permissions, moduleName, submoduleName, type.name, 'edit', gender);
    const canAdd = hasActionPermission(permissions, moduleName, submoduleName, type.name, 'add', gender);
    const canPrint = hasActionPermission(permissions, moduleName, submoduleName, type.name, 'print', gender);
    const isMale = type.permissions?.some(p => p.male) || false;
    const isFemale = type.permissions?.some(p => p.female) || false;
    result[type.name] = { canView, canEdit, canAdd, canPrint, isMale, isFemale };
  }
  return result;
}


/**
 * Returns the default clinical redirection path for a user.
 * If a patientId is provided and the user has access, returns the first enabled clinical summary section for that patient.
 * Otherwise, returns the default clinical dashboard path.
 */
export function getDefaultClinicalRedirect(patientId?: number): string {
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) return '/clinical/dashboard';

  const clinicalModule = permissions.modules.find(m => m.name === 'Clinical' && m.featureEnabled);
  if (!clinicalModule) return '/clinical/dashboard';

  // Check if user has access to Clinical Dashboard submodule
  const hasDashboardAccess = clinicalModule.submodules.some(s => s.name === 'Dashboard' && s.featureEnabled);

  if (patientId && patientId > 0) {
    for (const item of clinicalSummaryPriority) {
      const submodule = clinicalModule.submodules.find(s => s.name === item.name && s.featureEnabled);
      if (submodule) {
        return item.path(patientId);
      }
    }
  }

  // If no dashboard access, navigate to /clinical/patients
  return hasDashboardAccess ? '/clinical/dashboard' : '/clinical/patients';
}



/**
 * Returns the default Lab submodule path based on user permissions.
 * Checks allowedLabSubmodules in priority order and returns the first accessible path.
 * If none are accessible, returns '/lab/dashboard'.
 */
export function getDefaultLabSubmoduleRedirect(patientId?: number): string {
  // Usage example for Embryology:
  // const permissions = getPermissionsFromLocalStorage();
  // if (permissions && row.patient_id > 0) {
  //   redirectToFirstEmbryology(permissions.modules, row.patient_id, navigateTo);
  // } else if (row.patient_id > 0) {
  //   navigateTo(`/lab/embryology/oocyte-grading/${row.patient_id}`);
  // } else {
  //   navigateTo('/lab/embryology');
  // }
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) return '/lab/dashboard';
  const labModule = permissions.modules.find(m => m.name === 'Lab' && m.featureEnabled);
  if (!labModule) return '/lab/dashboard';
  const allowedLabSubmodules = labModule.submodules.filter(s => s.featureEnabled).map(s => s.name);

  // Check if user has access to Lab Dashboard submodule
  const hasDashboardAccess = labModule.submodules.some(s => s.name === 'Dashboard' && s.featureEnabled);

  // Priority order for Lab submodules 
  const priorityOrder: Record<string, (id?: number) => string> = {
    'Embryology': (id) => {
      if (id && id > 0 && labModule) {
        // Try each embryology feature in order
        for (const item of embryologyPriority) {
          const submodule = labModule.submodules.find(s => s.name === item.name && s.featureEnabled);
          if (submodule) {
            return item.path(id);
          }
        }
        // If no feature access, fallback
        return '/lab/embryology';
      }
      return '/lab/embryology';
    },
    'Andrology': (id) => id && id > 0 ? `/lab/andrology/semen-analysis/${id}` : '/lab/andrology',
  };

  // If patientId is not valid, always use dashboard or fallback
  const isValidPatientId = patientId && patientId > 0;
  if (!isValidPatientId) {
    return hasDashboardAccess ? '/lab/dashboard' : '/lab/embryology';
  }
  for (const submodule of Object.keys(priorityOrder)) {
    if (allowedLabSubmodules.includes(submodule)) {
      return priorityOrder[submodule](patientId);
    }
  }
  return hasDashboardAccess ? '/lab/dashboard' : '/lab/embryology';
}

/**
  * Returns an array of allowed Lab section names for sidebar display.
  * Only includes Lab submodules that are featureEnabled in permissions.
  */
export function getAllowedLabSidebarSections(): string[] {
  const permissions = getPermissionsFromLocalStorage();
  if (!permissions) return [];
  const labModule = permissions.modules.find(m => m.name === 'Lab' && m.featureEnabled);
  if (!labModule) return [];
  // Only show 'Andrology' and 'Embryology' if their 'Patient' type is featureEnabled
  return labModule.submodules
    .filter(sub => {
      if (!sub.featureEnabled) return false;
      if ((sub.name === 'Andrology' || sub.name === 'Embryology')) {
        return sub.type?.some(t => t.name === 'Patient' && t.featureEnabled);
      }
      return false;
    })
    .map(sub => sub.name);
}